/**
 * GitHub Contents API client.
 *
 * The console talks to GitHub directly — there is no backend. A fine-grained
 * PAT (contents: read/write, this repo only) is stored in localStorage.
 *
 * Write-back uses the Contents API's optimistic concurrency: we send the blob
 * `sha` we read, so a concurrent change fails loudly instead of silently
 * clobbering. On conflict we re-read and retry once.
 */

const API = "https://api.github.com";

export interface RepoConfig {
  owner: string;
  repo: string;
  branch: string;
  token: string;
}

export interface RemoteFile {
  path: string;
  sha: string;
  content: string;
}

export class GitHubError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "GitHubError";
  }
}

function headers(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
  };
}

/** Decode base64 content that may contain newlines (GitHub wraps at 60 chars). */
function decodeBase64(b64: string): string {
  const clean = b64.replace(/\s/g, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}

/** Encode UTF-8 text to base64. */
function encodeBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** Verify the token and return the repo's default branch. */
export async function verifyAccess(cfg: RepoConfig): Promise<{ defaultBranch: string }> {
  const res = await fetch(`${API}/repos/${cfg.owner}/${cfg.repo}`, {
    headers: headers(cfg.token),
    cache: "no-store",
  });
  if (!res.ok) {
    throw new GitHubError(
      res.status === 401
        ? "Token rejected (401). Check the PAT is valid and not expired."
        : res.status === 404
          ? "Repo not found (404). Check owner/repo and that the token can see it."
          : `GitHub error ${res.status}`,
      res.status,
    );
  }
  const data = (await res.json()) as { default_branch: string };
  return { defaultBranch: data.default_branch };
}

/** List all file paths in the repo branch (recursive). */
export async function listRepoPaths(cfg: RepoConfig): Promise<string[]> {
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/git/trees/${cfg.branch}?recursive=1`,
    { headers: headers(cfg.token), cache: "no-store" },
  );
  if (!res.ok) throw new GitHubError(`Tree fetch failed (${res.status})`, res.status);
  const data = (await res.json()) as {
    tree: { path: string; type: string }[];
  };
  return data.tree.filter((n) => n.type === "blob").map((n) => n.path);
}

/** List all .md files under a directory (recursive, open/decided + housekeeping). */
export async function listItemPaths(cfg: RepoConfig): Promise<string[]> {
  const paths = await listRepoPaths(cfg);
  return paths.filter(isItemPath);
}

/** Item space: `agentic/<domain>/open|decided/` markdown (+ `housekeeping/`). */
export function isItemPath(p: string): boolean {
  return (
    /^agentic\/[^/]+\/(?:open|decided)\/(?:housekeeping\/)?[^/]+\.md$/.test(p) &&
    !p.startsWith("agentic/dev/")
  );
}

/** Question files: `agentic/<domain>/…/_questions/<id>.md` (area depth 0–2). */
export function isQuestionPath(p: string): boolean {
  return (
    /^agentic\/[^/]+\/(?:[^/]+\/)*_questions\/[^/]+\.md$/.test(p) &&
    !p.startsWith("agentic/dev/")
  );
}

/** Node metadata: `agentic/<domain>/<area…>/_node.yml`. */
export function isNodeMetaPath(p: string): boolean {
  return (
    /^agentic\/[^/]+\/(?:[^/]+\/){0,3}_node\.yml$/.test(p) &&
    !p.startsWith("agentic/dev/")
  );
}

export interface KnowledgePaths {
  items: string[];
  questions: string[];
  nodeMetas: string[];
}

/** One tree fetch → every path the console v2 needs. */
export async function listKnowledgePaths(cfg: RepoConfig): Promise<KnowledgePaths> {
  const paths = await listRepoPaths(cfg);
  return {
    items: paths.filter(isItemPath),
    questions: paths.filter(isQuestionPath),
    nodeMetas: paths.filter(isNodeMetaPath),
  };
}

/**
 * The refresh backbone: ONE call returns every blob path with its SHA, so a
 * refresh only re-reads files whose SHA changed (the old design fetched all
 * ~300 files per refresh and burned the API budget — "Tree could not be
 * loaded").
 */
export async function listRepoTree(cfg: RepoConfig): Promise<Map<string, string>> {
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/git/trees/${cfg.branch}?recursive=1`,
    { headers: headers(cfg.token), cache: "no-store" },
  );
  if (!res.ok) throw new GitHubError(`Tree fetch failed (${res.status})`, res.status);
  const data = (await res.json()) as {
    tree?: { path: string; type: string; sha: string }[];
  };
  const out = new Map<string, string>();
  for (const node of data.tree ?? []) {
    if (node.type === "blob") out.set(node.path, node.sha);
  }
  return out;
}

/** One commit as the Activity "Commits" tab needs it. */
export interface CommitInfo {
  sha: string;
  message: string;
  date: string;
  author: string;
}

/** The last commits on the branch (the console's + phone's + nightly's audit trail). */
export async function listCommits(cfg: RepoConfig, perPage = 30): Promise<CommitInfo[]> {
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/commits?sha=${encodeURIComponent(cfg.branch)}&per_page=${perPage}`,
    { headers: headers(cfg.token), cache: "no-store" },
  );
  if (!res.ok) throw new GitHubError(`Commit list failed (${res.status})`, res.status);
  const data = (await res.json()) as {
    sha: string;
    commit: { message: string; author: { name?: string; date?: string } | null };
  }[];
  return data.map((c) => ({
    sha: c.sha,
    message: (c.commit.message || "").split("\n")[0],
    date: c.commit.author?.date ?? "",
    author: c.commit.author?.name ?? "",
  }));
}

/**
 * Blob content URL (Git Data API). NOTE: reads intentionally do NOT go through
 * `raw.githubusercontent.com` — raw rejects the CORS preflight for an
 * `Authorization` header (OPTIONS → 403 without Access-Control-Allow-Headers),
 * so in the browser every authorized raw read fails before it is even sent.
 * `api.github.com` answers the preflight properly (allow-headers: Authorization)
 * and is the same host the tree fetch uses. Reads are addressed by blob SHA,
 * so they are immutable and never need a cache-buster.
 */
export function blobUrl(cfg: RepoConfig, sha: string): string {
  return `${API}/repos/${cfg.owner}/${cfg.repo}/git/blobs/${sha}`;
}

/** Read one file's text by blob SHA (`Accept: application/vnd.github.raw`). */
export async function readFileAtSha(
  cfg: RepoConfig,
  path: string,
  sha: string,
): Promise<RemoteFile> {
  const res = await fetch(blobUrl(cfg, sha), {
    headers: { ...headers(cfg.token), Accept: "application/vnd.github.raw" },
    cache: "no-store",
  });
  if (!res.ok) throw new GitHubError(`Blob read failed for ${path} (${res.status})`, res.status);
  return { path, sha, content: await res.text() };
}

export interface BatchFile {
  path: string;
  /** null = delete this path in the commit. */
  content: string | null;
}

/** Tree entries for a batch commit (pure — unit-tested). */
export function commitTreeEntries(files: BatchFile[]): {
  path: string;
  mode: string;
  type: string;
  content?: string;
  sha?: null;
}[] {
  return files.map((f) =>
    f.content === null
      ? { path: f.path, mode: "100644", type: "blob", sha: null }
      : { path: f.path, mode: "100644", type: "blob", content: f.content },
  );
}

/**
 * ONE commit for any number of files (Git Data API, ~5 requests total):
 * ref → base commit → new tree (inline contents) → commit → ref update.
 * Returns the new commit SHA and the new blob SHAs per path.
 *
 * Deletion no-ops are dropped first: GitHub answers `sha: null` for a path that
 * does NOT exist in the base tree with 422 `GitRPC::BadObjectState` — which
 * would kill the whole batch (e.g. tapping “Standard” on an area that never had
 * a `_node.yml`). When every change is such a no-op, no commit is created.
 */
export async function commitFiles(
  cfg: RepoConfig,
  files: BatchFile[],
  message: string,
): Promise<{ commitSha: string; shas: Map<string, string> }> {
  const json = { ...headers(cfg.token), "Content-Type": "application/json" };
  const repoApi = `${API}/repos/${cfg.owner}/${cfg.repo}`;

  const refRes = await fetch(`${repoApi}/git/ref/heads/${encodeURIComponent(cfg.branch)}`, {
    headers: headers(cfg.token),
    cache: "no-store",
  });
  if (!refRes.ok) throw new GitHubError(`Ref read failed (${refRes.status})`, refRes.status);
  const headSha = ((await refRes.json()) as { object: { sha: string } }).object.sha;

  const commitRes = await fetch(`${repoApi}/git/commits/${headSha}`, {
    headers: headers(cfg.token),
    cache: "no-store",
  });
  if (!commitRes.ok) throw new GitHubError(`Head commit read failed (${commitRes.status})`, commitRes.status);
  const baseTree = ((await commitRes.json()) as { tree: { sha: string } }).tree.sha;

  let filesToWrite = files;
  if (files.some((f) => f.content === null)) {
    const listingRes = await fetch(
      `${repoApi}/git/trees/${baseTree}?recursive=1`,
      { headers: headers(cfg.token), cache: "no-store" },
    );
    if (!listingRes.ok) {
      throw new GitHubError(`Tree listing failed (${listingRes.status})`, listingRes.status);
    }
    const listing = (await listingRes.json()) as { tree?: { path: string }[] };
    const existing = new Set((listing.tree ?? []).map((n) => n.path));
    filesToWrite = files.filter((f) => f.content !== null || existing.has(f.path));
  }
  if (filesToWrite.length === 0) {
    // Every staged change was a deletion of something that does not exist:
    // nothing to commit — report the current head, no ref movement.
    return { commitSha: headSha, shas: new Map() };
  }

  const treeRes = await fetch(`${repoApi}/git/trees`, {
    method: "POST",
    headers: json,
    body: JSON.stringify({ base_tree: baseTree, tree: commitTreeEntries(filesToWrite) }),
  });
  if (!treeRes.ok) {
    throw new GitHubError(`Tree write failed (${treeRes.status}): ${await treeRes.text()}`, treeRes.status);
  }
  const tree = (await treeRes.json()) as { sha: string; tree: { path: string; sha: string }[] };

  const newCommitRes = await fetch(`${repoApi}/git/commits`, {
    method: "POST",
    headers: json,
    body: JSON.stringify({ message, tree: tree.sha, parents: [headSha] }),
  });
  if (!newCommitRes.ok) {
    throw new GitHubError(
      `Commit failed (${newCommitRes.status}): ${await newCommitRes.text()}`,
      newCommitRes.status,
    );
  }
  const newCommit = ((await newCommitRes.json()) as { sha: string }).sha;

  const patchRes = await fetch(`${repoApi}/git/refs/heads/${encodeURIComponent(cfg.branch)}`, {
    method: "PATCH",
    headers: json,
    body: JSON.stringify({ sha: newCommit, force: false }),
  });
  if (!patchRes.ok) {
    // Another writer won the race (e.g. a nightly run merged meanwhile): the
    // staged edits stay staged, the founder can simply confirm again.
    throw new GitHubError(
      `Branch update rejected (${patchRes.status}) — the branch moved; confirm again.`,
      patchRes.status,
    );
  }

  const shas = new Map<string, string>();
  for (const entry of tree.tree ?? []) {
    if (filesToWrite.some((f) => f.path === entry.path && f.content !== null)) {
      shas.set(entry.path, entry.sha);
    }
  }
  return { commitSha: newCommit, shas };
}

/** Read one file. */
export async function readFile(cfg: RepoConfig, path: string): Promise<RemoteFile> {
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/contents/${encodeURI(path)}?ref=${cfg.branch}`,
    { headers: headers(cfg.token), cache: "no-store" },
  );
  if (!res.ok) throw new GitHubError(`Read failed for ${path} (${res.status})`, res.status);
  const data = (await res.json()) as { sha: string; content: string };
  return { path, sha: data.sha, content: decodeBase64(data.content) };
}

/** Read one file; `null` when it does not exist (404). */
export async function readFileOrNull(
  cfg: RepoConfig,
  path: string,
): Promise<RemoteFile | null> {
  try {
    return await readFile(cfg, path);
  } catch (e) {
    if (e instanceof GitHubError && e.status === 404) return null;
    return null;
  }
}

/** Read many files with bounded concurrency. */
export async function readFiles(
  cfg: RepoConfig,
  paths: string[],
  concurrency = 6,
): Promise<RemoteFile[]> {
  const out: RemoteFile[] = [];
  let i = 0;
  async function worker() {
    while (i < paths.length) {
      const idx = i++;
      try {
        out.push(await readFile(cfg, paths[idx]));
      } catch {
        // Skip unreadable files rather than failing the whole inbox.
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, paths.length) }, worker));
  return out;
}

/**
 * Write one file. Retries once on a 409 conflict by re-reading the sha.
 * Returns the new sha.
 */
export async function writeFile(
  cfg: RepoConfig,
  path: string,
  content: string,
  message: string,
  sha?: string,
): Promise<string> {
  const body = {
    message,
    content: encodeBase64(content),
    branch: cfg.branch,
    ...(sha ? { sha } : {}),
  };
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/contents/${encodeURI(path)}`,
    {
      method: "PUT",
      headers: { ...headers(cfg.token), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (res.status === 409 || res.status === 422) {
    // Conflict: someone else changed the file. Re-read and retry once.
    const fresh = await readFile(cfg, path);
    return writeFile(cfg, path, content, message, fresh.sha);
  }
  if (!res.ok) {
    const text = await res.text();
    throw new GitHubError(`Write failed for ${path} (${res.status}): ${text}`, res.status);
  }
  const data = (await res.json()) as { content: { sha: string } };
  return data.content.sha;
}

/** The PAT scope hint shown whenever Actions API calls fail with 403/404. */
export const ACTIONS_SCOPE_HINT =
  "This needs a PAT with Actions: Read and write (fine-grained, this repo only). " +
  "Open Settings (gear) and paste one, then retry.";

/**
 * Delete a file (Contents API DELETE) — used when an item moves out of `open/`
 * (accept): the item is written to the domain's `decided/` folder and the old
 * file deleted; `done:*` must not live under `open/` (tools/check_items.py).
 */
export async function deleteFile(
  cfg: RepoConfig,
  path: string,
  message: string,
  sha?: string,
): Promise<void> {
  const currentSha = sha ?? (await readFile(cfg, path)).sha;
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/contents/${encodeURI(path)}`,
    {
      method: "DELETE",
      headers: { ...headers(cfg.token), "Content-Type": "application/json" },
      body: JSON.stringify({ message, sha: currentSha, branch: cfg.branch }),
    },
  );
  // 404 = already gone (moved by someone else) — that is the goal state.
  if (res.status === 404) return;
  if (!res.ok) {
    const text = await res.text();
    throw new GitHubError(`Delete failed for ${path} (${res.status}): ${text}`, res.status);
  }
}

/** Trigger a workflow by name on the configured branch. */
export async function dispatchWorkflow(
  cfg: RepoConfig,
  workflowFile: string,
  inputs: Record<string, string> = {},
): Promise<void> {
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/actions/workflows/${workflowFile}/dispatches`,
    {
      method: "POST",
      headers: { ...headers(cfg.token), "Content-Type": "application/json" },
      body: JSON.stringify({ ref: cfg.branch, inputs }),
    },
  );
  if (!res.ok) {
    const detail = res.status === 403 || res.status === 404 ? ` ${ACTIONS_SCOPE_HINT}` : "";
    throw new GitHubError(`Workflow dispatch failed (${res.status}).${detail}`, res.status);
  }
}

/** One workflow run, as far as the panels need it. */
export interface WorkflowRun {
  id: number;
  runNumber: number;
  status: string;
  conclusion: string | null;
  event: string;
  createdAt: string;
  updatedAt: string;
  htmlUrl: string;
}

/** Map a workflow-runs payload to runs (newest first). Pure; unit-tested. */
export function mapWorkflowRuns(data: unknown): WorkflowRun[] {
  const runs = (data as { workflow_runs?: unknown[] } | null)?.workflow_runs;
  if (!Array.isArray(runs)) return [];
  return runs.flatMap((r) => {
    const run = r as Record<string, unknown>;
    if (typeof run.id !== "number" || typeof run.html_url !== "string") return [];
    return [
      {
        id: run.id,
        runNumber: typeof run.run_number === "number" ? run.run_number : 0,
        status: typeof run.status === "string" ? run.status : "unknown",
        conclusion: typeof run.conclusion === "string" ? run.conclusion : null,
        event: typeof run.event === "string" ? run.event : "",
        createdAt: typeof run.created_at === "string" ? run.created_at : "",
        updatedAt: typeof run.updated_at === "string" ? run.updated_at : "",
        htmlUrl: run.html_url,
      },
    ];
  });
}

/**
 * Recent runs of a workflow (newest first). This is the surface that makes a
 * FAILED run visible: a failed run merges nothing, so `activity.jsonl` never
 * contains it (runs 35587100801 and 35981207864 stranded for days this way).
 */
export async function listWorkflowRuns(
  cfg: RepoConfig,
  workflowFile: string,
  perPage = 8,
): Promise<WorkflowRun[]> {
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/actions/workflows/${workflowFile}/runs?per_page=${perPage}`,
    { headers: headers(cfg.token), cache: "no-store" },
  );
  if (res.status === 404) return [];
  if (!res.ok) {
    const detail = res.status === 403 ? ` ${ACTIONS_SCOPE_HINT}` : "";
    throw new GitHubError(`Run list failed (${res.status}).${detail}`, res.status);
  }
  return mapWorkflowRuns(await res.json());
}

/** The latest run of a workflow (null when the workflow has never run). */
export async function latestWorkflowRun(
  cfg: RepoConfig,
  workflowFile: string,
): Promise<WorkflowRun | null> {
  const runs = await listWorkflowRuns(cfg, workflowFile, 1);
  return runs[0] ?? null;
}

/** An open PR on a `nightly/*` branch: a run whose merge never finished. */
export interface NightlyPr {
  number: number;
  title: string;
  headRef: string;
  createdAt: string;
  htmlUrl: string;
}

/** Map an open-PRs payload to the stranded `nightly/*` ones. Pure; unit-tested. */
export function mapNightlyPrs(data: unknown): NightlyPr[] {
  if (!Array.isArray(data)) return [];
  return data.flatMap((p) => {
    const pr = p as Record<string, unknown>;
    const head = (pr.head ?? {}) as Record<string, unknown>;
    const ref = typeof head.ref === "string" ? head.ref : "";
    if (!ref.startsWith("nightly/")) return [];
    if (typeof pr.number !== "number" || typeof pr.html_url !== "string") return [];
    return [
      {
        number: pr.number,
        title: typeof pr.title === "string" ? pr.title : "",
        headRef: ref,
        createdAt: typeof pr.created_at === "string" ? pr.created_at : "",
        htmlUrl: pr.html_url,
      },
    ];
  });
}

/** Open `nightly/*` PRs: runs that failed and left their drafts unmerged. */
export async function listOpenNightlyPrs(cfg: RepoConfig): Promise<NightlyPr[]> {
  const res = await fetch(
    `${API}/repos/${cfg.owner}/${cfg.repo}/pulls?state=open&per_page=50`,
    { headers: headers(cfg.token), cache: "no-store" },
  );
  if (!res.ok) throw new GitHubError(`PR list failed (${res.status})`, res.status);
  return mapNightlyPrs(await res.json());
}
